import React, { useState, useEffect } from 'react';
import { useStaticAsset } from '@leagueplatform/asset-config';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  StackLayout,
  Modal,
  Button,
  ParagraphText,
  FormField,
  TextInput,
  InputStatusMessage,
} from '@leagueplatform/genesis-core';
import { LoadingIndicator } from '@leagueplatform/web-common-components';

import {
  isValidatePhoneLength,
  formatPhoneNumber,
  isEmpty,
} from '../../utils/index';
import { SHARED_LIBRARY_ASSET_KEYS } from '../../types/shared-library.types';

export function PhoneNumberModal(props: any) {
  const {
    open,
    heading,
    cancelBtn,
    saveBtn,
    onClose,
    description,
    updateContactData,
    type,
    updateContactLoading,
    allContactNumberValue,
    contactDataLoading,
  } = props;

  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phNumError, setPhNumError] = useState<string>('');
  const updateErrorState = (value: string) => {
    if (isEmpty(phoneNumber) || isValidatePhoneLength(value)) {
      setPhNumError(
        'Please enter a valid phone number in the form of (xxx) xxx-xxxx. Only numerical characters are accepted.',
      );
    } else {
      setPhNumError('');
    }
  };

  const handleSaveBtnClick = () => {
    updateErrorState(phoneNumber);
    if (phNumError === '' && phoneNumber.length === 14) {
      updateContactData(phoneNumber, type);
      // setOpenSuccess(true);
    }
  };

  const onUpdateField = (event: any) => {
    const { value } = event.target;
    setPhoneNumber(value);
  };

  const onBlurField = (event: any) => {
    updateErrorState(event.target.value);
  };

  const onKeyPressField = (event: any) => {
    updateErrorState(event.target.value);
  };

  useEffect(() => {
    if (type === 'Mobile Phone' || type === 'Text Message') {
      setPhoneNumber(
        allContactNumberValue?.mobilePhone || allContactNumberValue?.textPhone,
      );
    } else if (type === 'Work Phone') {
      setPhoneNumber(allContactNumberValue?.workPhone);
    } else if (type === 'Home Phone') {
      setPhoneNumber(allContactNumberValue?.homePhone);
    }
  }, [type, allContactNumberValue]);

  return (
    <Box>
      <Modal.Root open={open}>
        <Modal.Content
          data-testid="mainModal"
          showCloseButton={false}
          css={
            {
              borderRadius: 'initial',
              background: '#fff',
              padding: '20px 30px',
              '.GDS-modal': {
                borderRadius: '$none',
                margin: '0px',
              },
              '@mobile': {
                padding: 16,
              },
              // '.GDS-c-bVpebC-igUcHly-css': {
              //   margin: '0px',
              // },
            } as GDSStyleObject
          }
          width={{
            '@initial': 'standard',
            '@laptop': '38%',
            '@mobileLandscape': '50%',
            '@tablet': '50%',
          }}
          // onInteractOutside={(e) => e.preventDefault()}
        >
          <StackLayout
            css={{
              textAlign: 'center',
              alignItems: 'center',
              opacity:
                updateContactLoading || contactDataLoading ? 0.1 : 'initial',
            }}
          >
            <Box
              css={
                {
                  margin: '$one',
                  backgroundColor: 'White',
                } as GDSStyleObject
              }
            >
              <img
                style={{ cursor: 'pointer' }}
                src={
                  useStaticAsset(
                    SHARED_LIBRARY_ASSET_KEYS.FAILURE_ICON,
                  ) as string
                }
                alt="right arrow icon"
                width="45px"
              />
              <HeadingText
                data-testid="phone-heading"
                level="3"
                size="sm"
                css={
                  {
                    paddingTop: '$one',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '32px',
                    fontSize: 24,
                    color: '#2C3135',
                  } as GDSStyleObject
                }
              >
                {heading}
              </HeadingText>
              <ParagraphText
                data-testid="phone-description"
                css={
                  {
                    margin: '$two $none $one $none',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '24px',
                    fontSize: 16,
                    color: '#2C3135',
                  } as GDSStyleObject
                }
              >
                {description}
              </ParagraphText>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                  } as GDSStyleObject
                }
              >
                <FormField
                  data-testid="phone-input"
                  id="PhoneNumber"
                  label={
                    ['Mobile Phone', 'Text Message'].includes(type)
                      ? 'Phone Number'
                      : `${type} Number`
                  }
                  css={
                    {
                      padding: '0px 15px $one 15px',
                      '@mobile': {
                        padding: 10,
                      },
                      '#PhoneNumber-status-message': {
                        textAlign: 'left',
                      },
                    } as GDSStyleObject
                  }
                  inputStatus={phNumError ? 'error' : undefined}
                  statusIconLabel={phNumError ? 'Error' : ''}
                  statusMessage={
                    <InputStatusMessage hideIcon>
                      {phNumError}
                    </InputStatusMessage>
                  }
                >
                  <TextInput
                    data-testid="phone-update"
                    name="phoneNumber"
                    id="phoneNumber"
                    placeholder="Phone Number"
                    onChange={onUpdateField}
                    value={formatPhoneNumber(phoneNumber)}
                    maxLength={14}
                    onBlur={onBlurField}
                    onKeyUp={onKeyPressField}
                  />
                </FormField>
              </Box>

              <StackLayout
                data-testid="button-section"
                horizontalAlignment="start"
                css={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Button
                  data-testid="cancel-btn"
                  size="small"
                  tabIndex={0}
                  css={
                    {
                      color: '#0074C9',
                      cursor: 'pointer',
                      fontWeight: '700',
                      fontSize: 16,
                      padding: '$one',
                      background: 'none',
                      '&:hover,&:active,&:focus': {
                        background: 'none',
                      },
                    } as GDSStyleObject
                  }
                  onClick={onClose}
                >
                  {cancelBtn}
                </Button>

                <Button
                  data-testid="save-btn"
                  size="small"
                  tabIndex={0}
                  css={
                    {
                      color: '#0074C9',
                      cursor: 'pointer',
                      fontWeight: '700',
                      fontSize: 16,
                      padding: '$one',
                      background: 'none',
                      '&:hover,&:active,&:focus': {
                        background: 'none',
                      },
                    } as GDSStyleObject
                  }
                  onClick={handleSaveBtnClick}
                >
                  {saveBtn}
                </Button>
              </StackLayout>
            </Box>
          </StackLayout>
          {(updateContactLoading || contactDataLoading) && <LoadingIndicator />}
        </Modal.Content>
      </Modal.Root>
    </Box>
  );
}
